import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

/**
 * Receita médica SNGPC anexada a um DAV (relação 1:1).
 * Enums seguem o XSD do SNGPC: TipoReceituario 1..5, UsoMedicamento 1=Humano/2=Vet,
 * UnidadeIdade 1=Anos/2=Meses, Sexo 1=M/2=F.
 */
export interface ReceitaMedicaData {
  id: number;
  movDavId: number;
  tipoReceituario: number;
  numeroNotificacao?: string | null;
  dtPrescricao: string;
  usoMedicamento: number;
  prescritorNome: string;
  prescritorRegistro: string;
  prescritorConselho: string;
  prescritorUf: string;
  compradorNome?: string | null;
  compradorTipoDocumento?: number | null;
  compradorNumeroDocumento?: string | null;
  compradorOrgaoExpedidor?: string | null;
  compradorUf?: string | null;
  pacienteNome?: string | null;
  pacienteIdade?: number | null;
  pacienteUnidadeIdade?: number | null;
  pacienteSexo?: number | null;
  pacienteCid?: string | null;
  usoProlongado?: string | null;
}

export type SalvarReceitaMedicaRequest = Omit<ReceitaMedicaData, "id" | "movDavId">;

export interface DavPendenteReceita {
  movDavId: number;
  numeroDav: number;
  dtEmissao: string;
  clienteNome: string;
  qtdItensControlados: number;
  temReceita: boolean;
  valorTotal: number;
  situacao: string;
}

/** Lista de DAVs do período com medicamentos controlados (triagem de receitas). */
export const useReceitasPendentes = (inicio: string, fim: string, apenasPendentes: boolean) => {
  return useQuery({
    queryKey: ["sngpc-receitas-pendentes", inicio, fim, apenasPendentes],
    queryFn: async () =>
      (await apiClient.get<DavPendenteReceita[]>("/api/sngpc/receitas-pendentes", {
        params: { inicio, fim, apenasPendentes },
      })).data,
    enabled: !!inicio && !!fim,
  });
};

/** Receita anexada a um DAV específico (GET 204 → null; PUT upsert). */
export const useReceitaDoDav = (davId: number | undefined) => {
  const qc = useQueryClient();
  const base = `/api/dav/${davId}/receita`;

  const query = useQuery({
    queryKey: ["sngpc-receita-dav", davId],
    queryFn: async () => {
      const resp = await apiClient.get<ReceitaMedicaData>(base);
      return resp.status === 204 ? null : resp.data;
    },
    enabled: !!davId,
  });

  const salvar = useMutation({
    mutationFn: async (body: SalvarReceitaMedicaRequest) =>
      (await apiClient.put<ReceitaMedicaData>(base, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sngpc-receita-dav", davId] });
      qc.invalidateQueries({ queryKey: ["sngpc-receitas-pendentes"] });
      toast.success("Receita salva!");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.mensagem ?? `Erro ao salvar receita: ${e.message}`),
  });

  return {
    receita: query.data ?? null,
    isLoading: query.isLoading,
    salvarReceita: salvar.mutateAsync,
    salvando: salvar.isPending,
  };
};
